import { DropdownOption } from '../types';

type SelectWarningCustomConfig = {
  SetDisabledOptionWarning?:
    | ((args: {
        field: string;
        row: unknown;
        option?: DropdownOption;
        options?: DropdownOption[];
      }) => boolean)
    | string[];
};

type ShouldHighlightDisabledOptionArgs = {
  customConfig?: SelectWarningCustomConfig;
  field: string;
  row: unknown;
  option?: DropdownOption;
  options?: DropdownOption[];
};

type HasDisabledSelectedOptionWarningArgs = {
  type?: string;
  name: string;
  initialValues?: Record<string, unknown> | null;
  options?: DropdownOption[];
  customConfig?: SelectWarningCustomConfig;
};

type DropdownsByField = Record<string, DropdownOption[] | undefined>;

type GetResolvedOptionsArgs = {
  field: string;
  allDropdowns?: DropdownsByField;
  customConfig?: {
    SetDropdownOptions?: (args: { field: string; options: DropdownOption[] }) => DropdownOption[];
  };
};

type HasDisabledSelectedOptionWarningForGridArgs = {
  field: string;
  value: unknown;
  row: unknown;
  colDef?: {
    type?: string;
    attrDataType?: string;
  };
  allDropdowns?: DropdownsByField;
  customConfig?: SelectWarningCustomConfig & GetResolvedOptionsArgs['customConfig'];
};

/**
 * Resolves dropdown options for a field by starting from configured dropdowns
 * and optionally applying a custom option {disabled : true} transformer from screen config.
 *
 * @param args - Option resolution inputs.
 * @param args.field - Field/column name.
 * @param args.allDropdowns - Dropdown options map keyed by field name.
 * @param args.customConfig - Optional screen custom config with SetDropdownOptions hook.
 * @returns Resolved single-select options for the given field.
 */
export function getResolvedOptions({ field, allDropdowns, customConfig }: GetResolvedOptionsArgs) {
  const baseOptions = (allDropdowns?.[field] ?? []).filter((item) => item?.value != null);

  if (typeof customConfig?.SetDropdownOptions !== 'function') {
    return baseOptions;
  }

  const filtered = customConfig.SetDropdownOptions({
    field,
    options: baseOptions,
  });

  return Array.isArray(filtered) ? filtered : baseOptions;
}

/**
 * Returns true when a disabled selected option should be highlighted.
 *
 * @param args - Disabled-option warning evaluation inputs.
 * @param args.customConfig - Optional screen custom config.
 * @param args.field - Field/column name being evaluated.
 * @param args.row - Current row values.
 * @param args.option - Currently selected option, if found.
 * @param args.options - Available options for the field.
 * @returns True when warning should be shown for a disabled selected option.
 */
export function shouldHighlightDisabledOption({
  customConfig,
  field,
  row,
  option,
  options,
}: ShouldHighlightDisabledOptionArgs) {
  if (!option?.disabled) {
    return false;
  }

  const highlightRule = customConfig?.SetDisabledOptionWarning;

  if (typeof highlightRule === 'function') {
    return !!highlightRule({ field, row, option, options });
  }

  if (Array.isArray(highlightRule)) {
    return highlightRule.includes(field);
  }

  return false;
}

/**
 * Form-side helper that checks whether the selected single-select value should
 * show a disabled-option warning.
 *
 * @param args - Form-specific warning evaluation inputs.
 * @param args.type - Field type, only singleSelect is supported.
 * @param args.name - Form field name.
 * @param args.initialValues - Current form values.
 * @param args.options - Field options passed by the form renderer.
 * @param args.customConfig - Optional screen custom config.
 * @returns True when form selection maps to a disabled option that must be highlighted.
 */
export function hasDisabledSelectedOptionWarning({
  type,
  name,
  initialValues,
  options,
  customConfig,
}: HasDisabledSelectedOptionWarningArgs) {
  if (type !== 'singleSelect') {
    return false;
  }

  const selectedOption = options?.find((item) => item.value == initialValues?.[name]);

  return shouldHighlightDisabledOption({
    customConfig,
    field: name,
    row: initialValues,
    option: selectedOption,
    options,
  });
}

/**
 * Grid-side helper that checks whether a single-select cell should show a
 * disabled-option warning.
 *
 * @param args - Grid-specific warning evaluation inputs.
 * @param args.field - Grid column field name.
 * @param args.value - Current cell value.
 * @param args.row - Current row model.
 * @param args.colDef - Column definition metadata.
 * @param args.allDropdowns - Dropdown options map keyed by field name.
 * @param args.customConfig - Optional screen custom config.
 * @returns True when grid cell selection maps to a disabled option that must be highlighted.
 */
export function hasDisabledSelectedOptionWarningForGrid({
  field,
  value,
  row,
  colDef,
  allDropdowns,
  customConfig,
}: HasDisabledSelectedOptionWarningForGridArgs) {
  const type = colDef?.type ?? colDef?.attrDataType;

  if (type !== 'singleSelect') {
    return false;
  }

  const options = getResolvedOptions({ field, allDropdowns, customConfig });
  if (!options.length) {
    return false;
  }

  const option = options.find((item) => item.value == value);
  return shouldHighlightDisabledOption({
    customConfig,
    field,
    row,
    option,
    options,
  });
}
